import { useEffect, useRef, useState, useMemo } from 'react'
import moment from 'moment'
import { closeCashReconciliation, getCashReconciliationPayments, rectifyPayment, updateModelById } from "../services/apiService"
import 'moment/locale/es'
import { checkPermission } from '../utils/backoffice-roles-data'
import { parseNum, getDateTimeString, sum } from '../utils/bookings-data'
import { statusTranslations } from '../utils/cash-reconciliation-data'
import { useDialog } from '../hooks/useDialog'
import { PaymentRow } from '../components/PaymentRow'

moment.locale('es')


const methodNames = {
    creditcard: 'Tarjeta',
    cash: 'Efectivo'
}

export const CashReconciliationsForm = ({data, sendToUpdate, responseMessage}) => {
    /**
     * A CASH RECONCILIATION CANNOT AND SHOULD NOT BE CREATED FROM HERE
     * THIS FORM IS ONLY FOR UPDATE PURPOSES
     */
    const [notes, setNotes] = useState('')
    const [payments, setPayments] = useState({
        rows: [],
        totalRows: 0
    })
    const [formData, setFormData] = useState({
        id: 0,
        venueId: 0,
        userId: 0,
        user: {name: ''},
        venue: {name: ''},
        rectifiedPayments: [],
        originId: 0,
        start: Date.now(),
        end: null,
        status: 'open',
        cashSales: 0,
        cardSales: 0,
        expectedCashSales: 0,
        finishAt: null,
        expectedCardSales: 0,
        notes: '',
        cashDiscrepancy: 0,
        cardDiscrepancy: 0,
    })
    const [formChanges, setFormChanges] = useState({
        checks: [],
        rectifications: []
    })

    const availableToClose = useMemo(() => {

        const endDate = new Date(formData.start)
        endDate.setDate(endDate.getDate() + 1)
        endDate.setHours(1, 0, 0, 0)

        return Date.now() > endDate.getTime()

    }, [formData])

    const allPayments = useMemo(() => 
        payments.rows.filter(p => !p.cashReconciliationId && p.venueId === formData.venueId).concat(formData.rectifiedPayments)
    , [payments, formData])

    const checkedPayments = useMemo(() => 
        allPayments
            .filter(p => {
                const lastChange = formChanges.checks.find(check => check.id === p.id)?.value
                return lastChange ?? p.checked
            })
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    , [allPayments, formChanges])

    const uncheckedPayments = useMemo(() => 
        allPayments
            .filter(p => {
                const lastChange = formChanges.checks.find(check => check.id === p.id)?.value
                return lastChange !== undefined ? !lastChange : !p.checked
            })
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    , [allPayments, formChanges])

    const paymentMetrics = useMemo(() => {

        const metrics = {cash: {qty: 0, amount: 0}, creditcard: {qty: 0, amount: 0}}
            allPayments
            .sort((a, b) => a.method.localeCompare(b.method))
            .forEach(payment => {

                metrics[payment.method] = metrics[payment.method] ||{qty: 0, amount: 0}
    
                metrics[payment.method].qty += 1
                metrics[payment.method].amount += payment.amount
            })
        return metrics

    }, [allPayments])

    const {cashDiscrepancy, cardDiscrepancy, cardSales, cashSales} = useMemo(() => {

        const {cash:cashChecked = 0, creditcard:creditcardChecked = 0} = sum(checkedPayments, {field: "amount", groupBy: "method"})

        const cashSales = cashChecked
        const cardSales = creditcardChecked

        const cashDiscrepancy = cashSales - paymentMetrics.cash.amount
        const cardDiscrepancy = cardSales - paymentMetrics.creditcard.amount

        return {cashDiscrepancy, cardDiscrepancy, cashSales, cardSales}
    }, [checkedPayments, paymentMetrics])

    const fieldsToSend = useRef(new Set())
    const firstTime = useRef(true)
    const dialog = useDialog()

    const getData = async () => {
        firstTime.current = false

        const response = await getCashReconciliationPayments(formData.venueId, formData.id)
        if (response.result === 'ok') setPayments({rows: response.data, totalRows: response.count})
    }

    const refreshFormData = async (rectificationData) => {

        let paymentToRectificate = allPayments.find(row => row.id === rectificationData.id)
        const paymentAlreadyRectified = formChanges.rectifications.find(rectification => rectification.id === rectificationData.id)

        if (paymentAlreadyRectified) {
            dialog.open('alert', {
                message: "Ya existe una rectificación de este pago, para evitar conflictos, guarde o cancele y haga entonces la rectificación."
            })
            return
        }

        setFormChanges(prev => ({
            ...prev, 
            rectifications: [...prev.rectifications, rectificationData]
        }))

        setFormData(prev => {

            if (!paymentToRectificate) return prev

            const newRectificationsState = [...prev.rectifiedPayments]
            newRectificationsState.push({
                ...paymentToRectificate,
                amount: paymentToRectificate.amount * -1,
                cashReconciliationId: rectificationData.cashReconciliationId
            })

            if (rectificationData.venueId === formData.venueId) newRectificationsState.push({
                ...paymentToRectificate,
                amount: rectificationData.amount,
                method: rectificationData.method,
                type: rectificationData.type,
                cashReconciliationId: rectificationData.cashReconciliationId
            })

            console.log(paymentToRectificate, newRectificationsState)

            return {
                ...prev,
                rectifiedPayments: newRectificationsState
            }
        })

        checkPayment(rectificationData)
    }

    const handleInputChange = (event) => {

        let {target: {value, name}} = event
        fieldsToSend.current.add(name)
        const numbers = ['cashSales','cardSales']
        if (numbers.includes(name)) value = Number(value)

        if (name === 'notes') setNotes(value)
        else setFormData(prev => ({...prev, [name]: value}))
    }

    const handleSave = async () => {

        if (!formData.notes && (cashDiscrepancy || cardDiscrepancy) && !notes) {
            alert("En caso contrario a guardar por primera vez una caja sin descuadre, debe indicar el motivo del guardado en comentarios.")
        } else {

            await sendToUpdate({notes: notes.trim(), ...formChanges})
            window.location.reload()
        }
    }

    const handleCloseCashReconciliation = async () => {
        await closeCashReconciliation(formData.id)
        window.location.reload()
    }

    const checkPayment = (payment) => {
        const toPush = {id: payment.id, value: true}
        const mod = 1
        const name = payment.method === 'cash' ? 'cashSales' : 'cardSales'
        setFormChanges(prev => ({
            ...prev,
           checks: [...prev.checks.filter(p => p.id !== payment.id), toPush]
        }))
        handleInputChange({target: {name, value: formData[name] + (mod * payment.amount)}})
    }
    const uncheckPayment = (payment) => {
        const toPush = {id: payment.id, value: false}
        const mod = -1
        const name = payment.method === 'cash' ? 'cashSales' : 'cardSales'
        setFormChanges(prev => ({
            ...prev,
            checks: [...prev.checks.filter(p => p.id !== payment.id), toPush]
        }))
        handleInputChange({target: {name, value: formData[name] + (mod * payment.amount)}})

    }

    useEffect(() => {
        if (data && data.id > 0) setFormData(prev => ({...data, rules: { ...prev.rules, ...data.rules}}))
    }, [data])

    useEffect(() => {
        if (formData.id > 0 && firstTime.current) getData()
    }, [formData])//eslint-disable-line react-hooks/exhaustive-deps

	return  formData.id === 0 ? null : (
		<form className='pricing_rule_form'>
            {   formData.status === 'open' ?
                <div className='warning-box'>
                    <p>No se puede realizar ninguna modificación mientras la caja esté abierta</p>
                </div>
                :
                <div>
                    <div className='left_side'>
                        <div className="input_group">
                            <div>
                                <p className="label">Usuario</p>
                                <input type="text" name="userId" className="form-control" id="cash-reconciliation-userId" placeholder="Id" value={formData.user?.name} disabled onChange={()=>{/* prevents the error trigger */}} />
                            </div>
                            <div>
                                <p className="label">Estado de caja</p>
                                <div className={`cash-reconciliation-status-block ${formData.status}`}>
                                    <p>{statusTranslations[formData.status]}</p>
                                </div>
                            </div>
                        </div>
                        <div className="input_group">
                            <div>
                                <p className="label">Id de caja</p>
                                <input type="text" name='id' className="form-control" id="cash-reconciliation-id" placeholder="Id" value={formData.id} disabled onChange={()=>{/* prevents the error trigger */}} />
                            </div>
                            <div>
                                <p className="label">Tablao</p>
                                <input type="text" name='venueId' className="form-control" id="cash-reconciliation-venueId" placeholder="Id" value={formData.venue.name} disabled onChange={()=>{/* prevents the error trigger */}} />
                            </div>
                        </div>
                        <div className="input_group">
                            <div>
                                <p className="label">Apertura</p>
                                <input type="text" name='start' className="form-control" id="cash-reconciliation-start" placeholder="Id" value={getDateTimeString(formData.start)} disabled onChange={()=>{/* prevents the error trigger */}} />
                            </div>
                            <div>
                                <p className="label">Cierre</p>
                                <input type="text" name="end" className="form-control" id="cash-reconciliation-end" placeholder="Id" value={formData.end ? getDateTimeString(formData.end) : 'Sin cerrar'} disabled onChange={()=>{/* prevents the error trigger */}} />
                            </div>
                        </div>
                        <div className="input_group">
                            <div>
                                <p className="label">Comentarios</p>
                                {formData.notes && (
                                    <p className='previous-coments' dangerouslySetInnerHTML={{__html: formData.notes.split('\n').join('<br>')}}
                                    ></p>
                                )}
                                <textarea name="notes" className="form-control" id="cash-reconciliation-notes" placeholder="comentarios" value={notes} onChange={handleInputChange} />
                            </div>
                        </div>
                    </div>


                    <div className='right_side'>
                        <div className="input_group">
                            <div>
                                <p className="label">Descuadre Efectivo</p>
                                <input type="text" name='discrepancy' className="form-control" id="cash-reconciliation-discrepancy" placeholder="Id" value={cashDiscrepancy} disabled onChange={()=>{/* prevents the error trigger */}} />
                            </div>
                            <div>
                                <p className="label">Descuadre Tarjeta</p>
                                <input type="text" name='discrepancy' className="form-control" id="cash-reconciliation-discrepancy" placeholder="Id" value={cardDiscrepancy} disabled onChange={()=>{/* prevents the error trigger */}} />
                            </div>
                        </div>
                        <div className="input_group">
                            <div>
                                <p className="label">Efectivo Contado</p>
                                <input disabled type="number" name='cashSales' className="form-control" id="cash-reconciliation-cashSales" placeholder="Id" value={cashSales} onChange={handleInputChange} />
                            </div>
                            <div>
                                <p className="label">Tarjeta Contado</p>
                                <input disabled type="number" className="form-control" name="cardSales" id="cash-reconciliation-cardSales" placeholder="Id" value={cardSales} onChange={handleInputChange} />
                            </div>
                        </div>
                        <div className='input_group'>
                            <div>
                                <p className='label'>Cantidades esperadas basadas en la suma de los pagos.</p>
                                <div className='seller-payment-metrics'>
                                {
                                    Object.entries(paymentMetrics).map(([key, value]) => {

                                        return (
                                            <div key={key}>
                                                <p>{methodNames[key] || key}</p>
                                                <p>Pagos: <strong>{value.qty}</strong></p>
                                                <p>Dinero: <strong>{parseNum('EUR', value.amount)}</strong></p>
                                            </div>
                                        )
                                    })
                                }
                                </div>
                            </div>
                        </div>
                        {payments.rows.length > 0 && (
                            <>
                            <h5 className='raw-payment-list-title'>Listado de pagos</h5>
                            <p className='raw-payment-list-description'>Pagos realizados por {formData.user?.name} durante el periodo de apertura de la caja.</p>
                            <ul className='unchecked-payments-list raw-payment-list'>
                            {
                                uncheckedPayments.map((payment, index) => {
                                    if (payment.cashReconciliationId) return null

                                    const lastChange = formChanges.checks.find(check => check.id === payment.id)?.value
                                    const value = lastChange || payment.checked

                                    return (
                                        <PaymentRow 
                                            key={index}
                                            payment={payment}
                                            cashReconciliationId={formData.id}
                                            showRectifyButton
                                            showSwitch={true}
                                            switchValue={value}
                                            onRectified={refreshFormData}
                                            onSwitch={e => e.target.value ? checkPayment(payment) : uncheckPayment(payment)}
                                        />
                                    )
                                })
                            }
                            </ul>
                            <ul className="checked-payments-list raw-payment-list">
                            {
                                checkedPayments.map((payment, index) => {
                                    if (payment.cashReconciliationId) return null

                                    const lastChange = formChanges.checks.find(check => check.id === payment.id)?.value
                                    const value = lastChange || payment.checked

                                    return (
                                        <PaymentRow 
                                            key={index}
                                            payment={payment}
                                            cashReconciliationId={formData.id}
                                            showSwitch={true}
                                            switchValue={value}
                                            onSwitch={e => e.target.value ? checkPayment(payment) : uncheckPayment(payment)}
                                        />
                                    )
                                })
                            }
                            </ul>
                            </>
                        )}
                        {formData.rectifiedPayments.length > 0 && (
                            <>
                            <h5 className='raw-payment-list-title'>Pagos rectificados</h5>
                            <ul className='unchecked-payments-list raw-payment-list'>
                            {
                                formData.rectifiedPayments.map((payment, index) => {
                                    return (
                                        <PaymentRow key={index} payment={payment} cashReconciliationId={formData.id} showRectifyButton onRectified={refreshFormData}/>
                                    )
                                })
                            }
                            </ul>
                            </>
                        )}
                    </div>
                </div>
            }


           {    formData.id>0 && ( 
                <div className='update_info'>
				    Actualizado: {moment(formData.updatedAt).fromNow()} | Creado: {moment(formData.createdAt).fromNow()}
                </div>
            )}

            {   checkPermission('backoffice.cashReconciliation.write') && 
                formData.status !== 'open' && 
                !formData.finishAt && (
                <>
                <button type="button" className="btn rmb-btn-primary mt-3" onClick={() => handleSave(false)}>
                    Guardar
                </button>
                <button type="button" className="btn rmb-btn-secondary mt-3" onClick={() => window.location.reload()}>
                    Cancelar
                </button>
                </>
            )}
            { 
                checkPermission('backoffice.cashReconciliation.write') && 
                formData.status === 'open' && 
                availableToClose && (
                <button type="button" className="btn btn-danger mt-3" onClick={handleCloseCashReconciliation}>
                    Cerrar caja manualmente
                </button>
            )}

            {responseMessage && (
                <div className="mt-4 alert alert-success" role="alert">
                    {responseMessage}
                </div>
            )}
        </form>
		
	)
}